// Contestações do Memor.IA — página do adm.
// Lista as contestações/feedbacks agrupados por reunião, com filtros e indicadores,
// e abre a análise completa lado a lado com as contestações e o prompt do tipo de reunião.
// Aberta pelo e-mail com ?codigo=<reunião>&id=<contestação>: filtra e abre o detalhe.
// Backend: core/views_memora_contestacao.py
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import ReuniaoAnaliseModal from '../components/ReuniaoAnaliseModal';
import { ItemContestacao } from '../components/ContestacoesGestor';
import { STATUS_CONTESTACAO } from '../components/ContestacaoAnalise';
import { labelTipoReuniao } from '../utils/competencias';

const API = process.env.REACT_APP_API_BASE;
const authHeader = (params) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, params });
const fmtNota = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 1 }));
const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};

const TIPOS_REUNIAO = [
  { key: 'servico', label: 'Serviço / Consultivo' },
  { key: 'comercial', label: 'Comercial / Vendas' },
  { key: 'cs', label: 'CS / Retenção' },
  { key: 'gestao', label: 'Gestão de Pessoas / 1:1' },
  { key: 'vision', label: 'Reunião Vision' },
  { key: 'reforma_tributaria', label: 'Reforma Tributária' },
];

const FILTROS_VAZIOS = { status: '', tipo_reuniao: '', id_usuario: '', data_de: '', data_ate: '', q: '' };

function Kpi({ rotulo, valor, icone, classe = 'text-muted' }) {
  return (
    <div className="col-6 col-md-4 col-xl-2">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body py-3">
          <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
            <i className={`fas ${icone} me-1 ${classe}`}></i>{rotulo}
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#212529', fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
        </div>
      </div>
    </div>
  );
}

function PromptUsado({ tipo }) {
  const [aberto, setAberto] = useState(false);
  const [estado, setEstado] = useState({});

  useEffect(() => {
    setAberto(false);
    setEstado({});
  }, [tipo]);

  const alternar = async () => {
    const abrir = !aberto;
    setAberto(abrir);
    if (!abrir || estado.prompt || estado.carregando) return;
    setEstado({ carregando: true });
    try {
      const r = await axios.get(`${API}/api/reunioes/contestacoes/prompt/${encodeURIComponent(tipo)}/`, authHeader());
      setEstado({ prompt: r.data?.prompt });
    } catch (e) {
      setEstado({ erro: e.response?.data?.erro || 'Não foi possível carregar o prompt.' });
    }
  };

  if (!tipo) return null;
  return (
    <div className="border rounded p-2 mt-3">
      <button type="button" className="btn btn-link btn-sm px-0 text-decoration-none" onClick={alternar}>
        <i className={`fas fa-chevron-${aberto ? 'up' : 'down'} me-1`}></i>
        <i className="fas fa-file-code me-1"></i>Prompt usado ({labelTipoReuniao(tipo)})
      </button>
      {aberto && (
        <div className="mt-2">
          {estado.carregando && (
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              <span className="spinner-border spinner-border-sm me-2"></span>Carregando...
            </div>
          )}
          {estado.erro && <div className="alert alert-warning py-1 px-2 mb-0" style={{ fontSize: '0.8rem' }}>{estado.erro}</div>}
          {estado.prompt && (
            <>
              <div className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>
                {estado.prompt.nome_exibicao}
                {estado.prompt.atualizado_em && ` · atualizado em ${estado.prompt.atualizado_em}`}
                {!estado.prompt.ativo && ' · inativo'}
                {' · versão atual (pode ter mudado depois da análise)'}
              </div>
              <pre className="mb-0 p-2 rounded" style={{
                background: '#f8f9fa', fontSize: '0.75rem', maxHeight: 360, overflow: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'SFMono-Regular, Consolas, monospace',
              }}>{estado.prompt.prompt_template}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function MemoraContestacoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const codigoUrl = searchParams.get('codigo') || '';
  const idUrl = Number(searchParams.get('id')) || null;

  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [busca, setBusca] = useState('');
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [operadores, setOperadores] = useState({});
  const [codigoAberto, setCodigoAberto] = useState(null);
  const abriuDaUrl = useRef(false);

  // busca de texto com atraso, para não consultar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.q === busca ? f : { ...f, q: busca })), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    const params = {};
    Object.entries(filtros).forEach(([k, val]) => { if (val) params[k] = val; });
    if (codigoUrl) params.codigo = codigoUrl;
    try {
      const r = await axios.get(`${API}/api/reunioes/contestacoes/`, authHeader(params));
      const lista = r.data?.itens || [];
      setItens(lista);
      // a lista de operadores do filtro vai acumulando o que já apareceu
      setOperadores((prev) => {
        const novo = { ...prev };
        lista.forEach((x) => { if (x.id_usuario) novo[x.id_usuario] = x.operador || `#${x.id_usuario}`; });
        return novo;
      });
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível carregar as contestações.');
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, [filtros, codigoUrl]);

  useEffect(() => { carregar(); }, [carregar]);

  // aberto pelo link do e-mail: abre o detalhe da reunião uma vez
  useEffect(() => {
    if (codigoUrl && !abriuDaUrl.current && !carregando) {
      abriuDaUrl.current = true;
      setCodigoAberto(codigoUrl);
    }
  }, [codigoUrl, carregando]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    itens.forEach((x) => {
      if (!mapa.has(x.codigo_reuniao)) {
        mapa.set(x.codigo_reuniao, {
          codigo: x.codigo_reuniao, cliente: x.cliente_empresa, assunto: x.assunto,
          data: x.data_reuniao, tipo: x.tipo_reuniao, nota: x.nota_reuniao, itens: [],
        });
      }
      mapa.get(x.codigo_reuniao).itens.push(x);
    });
    return [...mapa.values()].map((g) => ({
      ...g,
      abertas: g.itens.filter((x) => x.status === 'aberta').length,
      operadores: [...new Set(g.itens.map((x) => x.operador).filter(Boolean))],
      ultima: g.itens.reduce((m, x) => (x.id > m ? x.id : m), 0),
    })).sort((a, b) => (b.abertas > 0) - (a.abertas > 0) || b.ultima - a.ultima);
  }, [itens]);

  const resumo = useMemo(() => {
    const cont = (s) => itens.filter((x) => x.status === s).length;
    const aceitas = cont('aceita');
    const mantidas = cont('mantida');
    const respondidas = aceitas + mantidas;
    const porTipo = {};
    itens.forEach((x) => {
      const k = x.tipo_reuniao || 'sem_tipo';
      porTipo[k] = (porTipo[k] || 0) + 1;
    });
    return {
      total: itens.length, abertas: cont('aberta'), aceitas, mantidas,
      pctAceitas: respondidas ? Math.round((aceitas / respondidas) * 100) : null,
      porTipo: Object.entries(porTipo).sort((a, b) => b[1] - a[1]),
    };
  }, [itens]);

  const onRespondido = (novo) => {
    if (!novo) { carregar(); return; }
    setItens((prev) => prev.map((x) => (x.id === novo.id ? { ...x, ...novo } : x)));
  };

  const setFiltro = (k, val) => setFiltros((f) => ({ ...f, [k]: val }));
  const limpar = () => {
    setFiltros(FILTROS_VAZIOS);
    setBusca('');
    if (codigoUrl) setSearchParams({});
  };
  const fecharReuniao = useCallback(() => setCodigoAberto(null), []);
  const grupoAberto = grupos.find((g) => g.codigo === codigoAberto);

  const lateral = codigoAberto ? (
    <div>
      <h6 className="fw-bold mb-2" style={{ color: '#212529' }}>
        <i className="fas fa-comment-dots me-2 text-muted"></i>Contestações desta reunião
      </h6>
      {!grupoAberto || grupoAberto.itens.length === 0 ? (
        <div className="text-muted" style={{ fontSize: '0.85rem' }}>Nenhuma contestação com os filtros atuais.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {grupoAberto.itens.map((it) => (
            <ItemContestacao
              key={it.id}
              item={it}
              onRespondido={onRespondido}
              destaque={it.id === idUrl}
              mostrarReuniao={false}
            />
          ))}
        </div>
      )}
      <PromptUsado tipo={grupoAberto?.tipo} />
    </div>
  ) : null;

  return (
    <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <i className="fas fa-comment-dots header-icon me-2"></i>
          <h2 className="titulo-pagina m-0">Contestações do Memor.IA</h2>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={carregar} disabled={carregando}>
          <i className={`fas fa-sync-alt me-1 ${carregando ? 'fa-spin' : ''}`}></i>Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Status</label>
          <select className="form-select form-select-sm" value={filtros.status} onChange={(e) => setFiltro('status', e.target.value)}>
            <option value="">Todas</option>
            <option value="aberta">Abertas</option>
            <option value="aceita">Aceitas</option>
            <option value="mantida">Mantidas</option>
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Tipo de reunião</label>
          <select className="form-select form-select-sm" value={filtros.tipo_reuniao} onChange={(e) => setFiltro('tipo_reuniao', e.target.value)}>
            <option value="">Todos</option>
            {TIPOS_REUNIAO.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Operador</label>
          <select className="form-select form-select-sm" value={filtros.id_usuario} onChange={(e) => setFiltro('id_usuario', e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(operadores).sort((a, b) => a[1].localeCompare(b[1])).map(([id, nome]) => (
              <option key={id} value={id}>{nome}</option>
            ))}
          </select>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Contestada de</label>
          <input type="date" className="form-control form-control-sm" value={filtros.data_de} onChange={(e) => setFiltro('data_de', e.target.value)} />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>até</label>
          <input type="date" className="form-control form-control-sm" value={filtros.data_ate} onChange={(e) => setFiltro('data_ate', e.target.value)} />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Busca</label>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Motivo, cliente, código..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>
      {(codigoUrl || Object.values(filtros).some(Boolean) || busca) && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3" style={{ fontSize: '0.85rem' }}>
          {codigoUrl && (
            <span className="badge bg-light text-dark border">
              <i className="fas fa-video me-1"></i>Reunião {codigoUrl}
            </span>
          )}
          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={limpar}>
            <i className="fas fa-times me-1"></i>Limpar filtros
          </button>
        </div>
      )}

      {/* Indicadores */}
      <div className="row g-3 mb-3">
        <Kpi rotulo="Total" valor={resumo.total} icone="fa-list" />
        <Kpi rotulo="Abertas" valor={resumo.abertas} icone="fa-hourglass-half" classe="text-warning" />
        <Kpi rotulo="Aceitas" valor={resumo.aceitas} icone="fa-check" classe="text-success" />
        <Kpi rotulo="Mantidas" valor={resumo.mantidas} icone="fa-balance-scale" />
        <Kpi rotulo="% aceitas" valor={resumo.pctAceitas === null ? '-' : `${resumo.pctAceitas}%`} icone="fa-percent" />
        <div className="col-6 col-md-4 col-xl-2">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                <i className="fas fa-layer-group me-1"></i>Por tipo de reunião
              </div>
              {resumo.porTipo.length === 0 ? <span className="text-muted">-</span> : resumo.porTipo.map(([k, n]) => (
                <div key={k} className="d-flex justify-content-between" style={{ fontSize: '0.82rem' }}>
                  <span>{k === 'sem_tipo' ? 'Sem tipo' : labelTipoReuniao(k)}</span>
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
        % aceitas considera só as contestações já respondidas.
      </div>

      {erro && <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.875rem' }}>{erro}</div>}

      {/* Lista por reunião */}
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.875rem' }}>
          <thead className="table-light">
            <tr>
              <th>Reunião</th>
              <th>Data</th>
              <th>Tipo</th>
              <th className="text-center">Nota da IA</th>
              <th>Operador(es)</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {carregando && itens.length === 0 && (
              <tr><td colSpan={7} className="text-muted text-center py-4">
                <span className="spinner-border spinner-border-sm me-2"></span>Carregando...
              </td></tr>
            )}
            {!carregando && grupos.length === 0 && (
              <tr><td colSpan={7} className="text-muted text-center py-4">Nenhuma contestação encontrada.</td></tr>
            )}
            {grupos.map((g) => {
              const statusGrupo = g.abertas > 0 ? 'aberta' : null;
              return (
                <tr key={g.codigo} style={{ cursor: 'pointer' }} onClick={() => setCodigoAberto(g.codigo)}>
                  <td>
                    <div className="fw-semibold" style={{ color: '#212529' }}>{g.codigo}</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {[g.cliente, g.assunto].filter(Boolean).join(' · ') || '-'}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtData(g.data)}</td>
                  <td><span className="badge bg-secondary">{labelTipoReuniao(g.tipo)}</span></td>
                  <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNota(g.nota)}</td>
                  <td>{g.operadores.join(', ') || '-'}</td>
                  <td>
                    {statusGrupo ? (
                      <span className={`badge ${STATUS_CONTESTACAO.aberta.cls}`}>{g.abertas} aberta{g.abertas > 1 ? 's' : ''}</span>
                    ) : (
                      <div className="d-flex flex-wrap gap-1">
                        {['aceita', 'mantida'].map((s) => {
                          const n = g.itens.filter((x) => x.status === s).length;
                          return n ? <span key={s} className={`badge ${STATUS_CONTESTACAO[s].cls}`}>{n} {STATUS_CONTESTACAO[s].label.toLowerCase()}</span> : null;
                        })}
                      </div>
                    )}
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{g.itens.length} contestaç{g.itens.length > 1 ? 'ões' : 'ão'}</div>
                  </td>
                  <td className="text-end">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); setCodigoAberto(g.codigo); }}>
                      <i className="fas fa-eye me-1"></i>Ver
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {codigoAberto && <ReuniaoAnaliseModal codigo={codigoAberto} onClose={fecharReuniao} lateral={lateral} />}
    </div>
  );
}
